import React, { useEffect, useState, useRef } from 'react';
import { useInView, useMotionValue, useTransform, animate } from 'framer-motion';
interface CountUpProps {
  value: string;
  duration?: number;
  className?: string;
}
export const CountUp = ({
  value,
  duration = 2,
  className = ''
}: CountUpProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.5
  });
  const [display, setDisplay] = useState('0');
  // Parse the numeric portion of the value (handles commas, decimals, plus signs, suffix like "YRS")
  const numericMatch = value.match(/[\d,]+\.?\d*/);
  const numericPart = numericMatch ? numericMatch[0] : '';
  const targetNumber = numericPart ? parseFloat(numericPart.replace(/,/g, '')) : 0;
  const hasDecimal = numericPart.includes('.');
  const hasComma = numericPart.includes(',');
  const prefix = numericMatch ? value.slice(0, numericMatch.index) : '';
  const suffix = numericMatch ? value.slice((numericMatch.index ?? 0) + numericPart.length) : value;
  useEffect(() => {
    if (!inView || !numericPart) {
      setDisplay(value);
      return;
    }
    const controls = animate(0, targetNumber, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: latest => {
        let formatted: string;
        if (hasDecimal) {
          formatted = latest.toFixed(1);
        } else {
          formatted = Math.round(latest).toString();
          if (hasComma && latest >= 1000) {
            formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }
        setDisplay(`${prefix}${formatted}${suffix}`);
      }
    });
    return () => controls.stop();
  }, [inView, targetNumber, value, duration, hasDecimal, hasComma, prefix, suffix, numericPart]);
  return <span ref={ref} className={className} data-id="element-668">
      {numericPart ? display : value}
    </span>;
};
export const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="text-acid-green font-barlow font-bold text-[10px] md:text-xs tracking-[4px] uppercase mb-4" data-id="element-669">
    — {children} —
  </div>;
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  fullWidth?: boolean;
}
export const Button = ({
  children,
  variant = 'primary',
  fullWidth,
  className = '',
  ...props
}: ButtonProps) => {
  const baseStyles = 'font-barlow font-bold uppercase tracking-[3px] transition-all duration-300 flex items-center justify-center';
  const widthStyles = fullWidth ? 'w-full' : 'px-8';
  const variants = {
    primary: 'bg-acid-green text-black clip-diagonal hover:bg-white py-4 text-sm',
    secondary: 'bg-transparent border border-acid-green text-acid-green hover:text-white hover:bg-acid-green-dim py-4 text-sm',
    ghost: 'bg-transparent border border-dark-3 text-text-body hover:text-white hover:border-acid-green py-4 text-sm'
  };
  return <button className={`${baseStyles} ${widthStyles} ${variants[variant]} ${className}`} {...props} data-id="element-670">
      {children}
    </button>;
};
export const Card = ({
  children,
  className = '',
  hover = true
}: {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
}) => <div className={`bg-dark-1 border border-dark-3 transition-all duration-300 ${hover ? 'hover:border-acid-green hover:-translate-y-1' : ''} ${className}`} data-id="element-671">
    {children}
  </div>;
export const HUDBrackets = () => <>
    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-acid-green opacity-50 pointer-events-none" data-id="element-672"></div>
    <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-acid-green opacity-50 pointer-events-none" data-id="element-673"></div>
    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-acid-green opacity-50 pointer-events-none" data-id="element-674"></div>
    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-acid-green opacity-50 pointer-events-none" data-id="element-675"></div>
  </>;
export const FlagIcon = ({
  className = 'w-6 h-4'
}: {
  className?: string;
}) => <svg viewBox="0 0 60 40" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-676">
    <rect width="60" height="40" fill="#0a1628" data-id="element-677" />
    <rect y="3" width="60" height="3" fill="#fff" data-id="element-678" />
    <rect y="9" width="60" height="3" fill="#fff" data-id="element-679" />
    <rect y="15" width="60" height="3" fill="#fff" data-id="element-680" />
    <rect y="21" width="60" height="3" fill="#fff" data-id="element-681" />
    <rect y="27" width="60" height="3" fill="#fff" data-id="element-682" />
    <rect y="33" width="60" height="3" fill="#fff" data-id="element-683" />
    <rect width="26" height="22" fill="#0a1628" data-id="element-684" />
    <g fill="#fff" data-id="element-685">
      {[4, 10, 16, 22].flatMap(y => [3, 9, 15, 21].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="0.8" data-id="element-686" />))}
    </g>
  </svg>;
export const StarIcon = ({
  className = 'w-3 h-3',
  filled = true
}: {
  className?: string;
  filled?: boolean;
}) => <svg viewBox="0 0 24 24" className={className} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" data-id="element-687">
    <polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" data-id="element-688" />
  </svg>;
export const TacticalDivider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex items-center gap-2 ${className}`} data-id="element-689">
    <span className="block w-12 h-[1px] bg-acid-green/40" data-id="element-690"></span>
    <span className="block w-1 h-1 bg-acid-green rotate-45" data-id="element-691"></span>
    <span className="block w-12 h-[1px] bg-acid-green/40" data-id="element-692"></span>
  </div>;